import { createContext, useContext, type ReactNode } from 'react';

/**
 * Wave 4 (D-503): what a surface needs switched on for the workspace — modules, packs, apps
 * (`module:fleet`, `app:tr.uetds`, `app:category:parcel` …). `requires` must all hold; `requiresAny`, when given,
 * needs at least one. The design system does not know what is enabled; the app passes the answer once.
 */
export interface Requirement {
  requires?: string | readonly string[];
  requiresAny?: readonly string[];
}

/** Answers whether a requirement holds in the current workspace. */
export type EnablementPredicate = (requirement: Requirement) => boolean;

const always: EnablementPredicate = () => true;
const EnablementContext = createContext<EnablementPredicate>(always);

export interface EnablementProviderProps {
  isEnabled: EnablementPredicate;
  children?: ReactNode;
}

/** Without a provider everything is enabled, so the package stays usable on its own. */
export function EnablementProvider({ isEnabled, children }: EnablementProviderProps) {
  return <EnablementContext.Provider value={isEnabled}>{children}</EnablementContext.Provider>;
}

export function useDsEnablement(): EnablementPredicate {
  return useContext(EnablementContext);
}

/** True when the thing declares nothing, or what it declares holds. */
export function meetsRequirement(isEnabled: EnablementPredicate, thing: Requirement | undefined | null): boolean {
  if (!thing || (thing.requires === undefined && (thing.requiresAny === undefined || thing.requiresAny.length === 0))) return true;
  return isEnabled(thing);
}

/** Keeps the items whose requirement holds; returns the same array when none declares one (stable identity). */
export function filterEnabled<T extends Requirement>(isEnabled: EnablementPredicate, items: readonly T[]): T[] {
  if (!items.some((i) => i.requires !== undefined || (i.requiresAny?.length ?? 0) > 0)) return items as T[];
  return items.filter((i) => meetsRequirement(isEnabled, i));
}
